/**
 * Stroke SVG glyphs for synthetic / web-ingest source types.
 *
 * Adapted from Roast `public-home-explainer-formats.tsx`. Shared by the unified
 * reader header (synthetic uploads) and the Gist `/try` create module.
 *
 * @file shared/source-format-icons.js
 */

/** @typedef {'url' | 'text' | 'file' | 'youtube' | 'googledoc' | 'media'} SourceFormatIconKind */

export const SourceFormatIconViewBox = '0 0 28 28';

const STROKE_ATTRS =
  'fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"';

const FILE_MARKUP = [
  '<path d="M6 2h11l5 5v19H6V2z"/>',
  '<path d="M17 2v5h5"/>',
  '<line x1="10" y1="13" x2="18" y2="13"/>',
  '<line x1="10" y1="17" x2="22" y2="17"/>',
  '<line x1="10" y1="21" x2="16" y2="21"/>'
].join('');

/** @type {Readonly<Record<SourceFormatIconKind, string>>} */
export const SourceFormatIconMarkup = Object.freeze({
  url: [
    '<circle cx="14" cy="14" r="10"/>',
    '<ellipse cx="14" cy="14" rx="4.5" ry="10"/>',
    '<line x1="4" y1="10" x2="24" y2="10"/>',
    '<line x1="4" y1="18" x2="24" y2="18"/>'
  ].join(''),
  text: [
    '<rect x="5" y="2" width="18" height="24"/>',
    '<line x1="9" y1="8" x2="19" y2="8"/>',
    '<line x1="9" y1="12" x2="19" y2="12"/>',
    '<line x1="9" y1="16" x2="19" y2="16"/>',
    '<line x1="9" y1="20" x2="14" y2="20"/>'
  ].join(''),
  file: FILE_MARKUP,
  googledoc: FILE_MARKUP,
  youtube: [
    '<rect x="4" y="6" width="20" height="16" rx="3"/>',
    '<path d="M12 10l8 4-8 4V10z"/>'
  ].join(''),
  media: [
    '<rect x="3" y="6" width="22" height="16"/>',
    '<circle cx="10" cy="12" r="2"/>',
    '<polyline points="3,20 11,14 16,18 25,10"/>'
  ].join('')
});

/**
 * @param {SourceFormatIconKind} kind
 * @param {{ className?: string; ariaHidden?: boolean }} [options]
 * @returns {string}
 */
/**
 * @param {unknown} value
 * @returns {value is SourceFormatIconKind}
 */
export function isSourceFormatIconKind(value) {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(SourceFormatIconMarkup, value);
}

export function renderSourceFormatIconSvg(kind, options = {}) {
  const markup = SourceFormatIconMarkup[kind];
  if (!markup) {
    throw new Error('source_format_icon_unknown');
  }
  const classAttr = options.className ? ` class="${options.className}"` : '';
  const aria = options.ariaHidden === false ? '' : ' aria-hidden="true"';
  // xmlns is REQUIRED: createSourceFormatIconElement parses this string with
  // DOMParser('image/svg+xml') (strict XML), which assigns no namespace without
  // an explicit xmlns — yielding a null-namespace element that is not a real
  // SVGSVGElement, so stroke/fill are ignored and the glyph never paints.
  // Harmless on the innerHTML/SSR path (the HTML parser assigns the ns anyway).
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${SourceFormatIconViewBox}"${classAttr}${aria} ${STROKE_ATTRS}>${markup}</svg>`;
}

/**
 * @param {SourceFormatIconKind} kind
 * @param {string} [className]
 * @returns {SVGSVGElement}
 */
export function createSourceFormatIconElement(kind, className) {
  const parsed = new DOMParser().parseFromString(
    renderSourceFormatIconSvg(kind, { className }),
    'image/svg+xml'
  );
  const node = document.importNode(parsed.documentElement, true);
  const isSvgElement = typeof SVGSVGElement !== 'undefined' && node instanceof SVGSVGElement;
  if (isSvgElement || node.localName === 'svg') {
    return /** @type {SVGSVGElement} */ (node);
  }
  throw new Error('source_format_icon_parse_failed');
}
